import React from 'react';
import { createStackNavigator } from '@react-navigation/stack';
import type { RootStackParamList } from '../types';

/**
 * Web counterpart to createRootStack.ts. @react-navigation/stack is a
 * pre-existing (previously unused) dependency — no install needed. Same
 * `Stack.Navigator`/`Stack.Screen` API as native-stack, so nothing else in
 * navigation/index.tsx needs to change.
 *
 * headerMode 'float': when a stack fills the whole browser window,
 * @react-navigation/stack switches cards to "page overflow" mode (min-height
 * 100%, no height cap) so document.body scrolls instead. Our body never
 * scrolls, so every pushed screen (venue profile, StudiZone, …) grew to its
 * content height and its own ScrollView couldn't scroll. 'float' disables
 * that mode; headers are hidden anyway, so nothing else changes.
 */
const BaseStack = createStackNavigator<RootStackParamList>();

type NavigatorProps = React.ComponentProps<typeof BaseStack.Navigator>;

function Navigator(props: NavigatorProps) {
  const { screenOptions } = props;
  const withFloat: NavigatorProps['screenOptions'] = typeof screenOptions === 'function'
    ? (args => ({ headerMode: 'float', ...screenOptions(args) }))
    : { headerMode: 'float', ...screenOptions };
  return React.createElement(BaseStack.Navigator, { ...props, screenOptions: withFloat });
}

export const Stack = { ...BaseStack, Navigator } as typeof BaseStack;
